import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Icons } from '../constants';
import { db } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useFirebase } from '../context/FirebaseContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const JoinArborLinkModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { user } = useFirebase();
  const [role, setRole] = useState<'WORKER' | 'EMPLOYER'>('WORKER');
  const [skills, setSkills] = useState<string>('');
  const [location, setLocation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    setIsSubmitting(true);
    try {
      const profileData = {
        name: user.displayName || user.email || 'Tree Care Pro',
        email: user.email,
        role: role,
        skills: skills.split(',').map(s => s.trim()).filter(s => s),
        location,
        createdAt: serverTimestamp(),
        bio: '',
        verifications: ['Email']
      };
      
      await setDoc(doc(db, 'arborlink_users', user.uid), profileData);
      onClose();
    } catch (error) {
      console.error('Failed to create ArborLink profile', error);
      alert('Error creating profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bark/60 backdrop-blur-sm">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-[32px] shadow-2xl w-full max-w-lg overflow-hidden"
        >
          <div className="bg-gecko p-6 text-white flex justify-between items-center relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-20 transform scale-150"><Icons.Tree /></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-black mb-1">Join ArborLink</h2>
              <p className="text-sm font-bold opacity-80 uppercase tracking-widest">Global Tree Care Network</p>
            </div>
            <button onClick={onClose} className="p-2 bg-white/10 hover:bg-white/20 rounded-xl transition relative z-10">
              <Icons.Close className="w-5 h-5" />
            </button>
          </div>
          
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">I am a...</label>
                <div className="grid grid-cols-2 gap-4">
                  <button 
                    type="button"
                    onClick={() => setRole('WORKER')}
                    className={`p-4 rounded-2xl border-2 font-bold transition-all ${role === 'WORKER' ? 'border-gecko bg-gecko/5 text-gecko' : 'border-stone-100 text-stone-500 hover:border-stone-200'}`}
                  >
                    Climber / Crew
                  </button>
                  <button 
                    type="button"
                    onClick={() => setRole('EMPLOYER')}
                    className={`p-4 rounded-2xl border-2 font-bold transition-all ${role === 'EMPLOYER' ? 'border-gecko bg-gecko/5 text-gecko' : 'border-stone-100 text-stone-500 hover:border-stone-200'}`}
                  >
                    Company / Employer
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Location</label>
                <input 
                  type="text" 
                  required
                  placeholder="City, State"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  className="w-full bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl font-bold text-bark focus:outline-none focus:border-gecko transition"
                />
              </div>

              {role === 'WORKER' && (
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Key Skills (comma separated)</label>
                  <input 
                    type="text" 
                    placeholder="E.g., Climber, Arborist, Stump Grinder"
                    value={skills}
                    onChange={e => setSkills(e.target.value)}
                    className="w-full bg-stone-50 border-2 border-stone-100 p-4 rounded-2xl font-bold text-bark focus:outline-none focus:border-gecko transition"
                  />
                </div>
              )}

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full py-4 bg-bark text-white rounded-2xl font-black tracking-widest uppercase hover:bg-stone-800 transition disabled:opacity-50"
              >
                {isSubmitting ? 'Creating Profile...' : 'Complete Profile'}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
